import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import supportIcon from '../../assets/supportIcon.png';
import empathieIcon from '../../assets/empathieIcon.png';
import clarteIcon from '../../assets/clarteIcon.png';
import expertiseIcon from '../../assets/expertiseIcon.png';
import resultatsIcon from '../../assets/resultatsIcon.png';
import multilingueIcon from '../../assets/multilingueIcon.png';
import photoProfile from '../../assets/IMG_1391-min.png';
import timeManagementIcon from '../../assets/timeManagementIco.png';

const ExpertiseAffirmation = () => {
  const { t, i18n } = useTranslation();
  
  // Get badges from translations
  const badges = t('expertise.badges', { returnObjects: true }) || [
    {
      icon: expertiseIcon,
      title: "Expertise",
      description: "Parajuriste en droit familial"
    },
    {
      icon: resultatsIcon,
      title: "Résultats concrets",
      description: "+150 familles aidées"
    },
    {
      icon: clarteIcon,
      title: "Clarté",
      description: "Des explications claires et accessibles"
    },
    {
      icon: multilingueIcon,
      title: "Multilingue",
      description: "Français, anglais, russe"
    }
  ];
  
  return (
    <section id="expertise" className="py-16 md:py-24 px-4 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-6"
          >
            {t('expertise.title')} <span className="text-primary">{t('expertise.location', 'Québec')}</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {t('expertise.subtitle')}
          </motion.p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-16">
          {badges.map((badge, index) => (
            <motion.div 
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-lg p-6 flex flex-col items-center text-center transform hover:-translate-y-2 transition-all duration-300"
            >
              <div className="p-3 bg-primary/10 rounded-full mb-4">
                <svg className="w-8 h-8 text-primary" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h4 className="text-xl font-bold mb-2 text-gray-800">{badge.title}</h4>
              <span className="text-gray-600">{badge.description}</span>
            </motion.div>
          ))}
        </div>
        
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="bg-white shadow-xl rounded-xl overflow-hidden mb-16"
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-0 md:gap-8">
            <div className="md:hidden bg-gradient-to-b from-primary/10 to-transparent pt-8 pb-4 px-4 text-center">
              <div className="inline-block relative">
                <div className="absolute inset-0 bg-warning/20 rounded-full transform rotate-6 scale-105"></div>
                <div className="relative overflow-hidden rounded-full border-4 border-warning w-40 h-40 mx-auto">
                  <img
                    src={photoProfile}
                    alt={t('expertise.founder.title')}
                    style={{
                      objectFit: 'cover',
                      objectPosition: 'center',
                      width: '100%',
                      height: '100%'
                    }}
                  />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mt-6">
                Raouf K.
              </h3>
              <p className="text-primary font-medium">{t('expertise.founder.role', 'Parajuriste en droit familial')}</p>
            </div>
            
            <div className="hidden md:flex md:col-span-4 items-center justify-center p-8 bg-gradient-to-br from-primary/5 to-warning/5">
              <div className="relative max-w-sm mx-auto">
                <div className="absolute inset-0 bg-warning/20 rounded-full transform rotate-6 scale-105"></div>
                <div className="relative overflow-hidden rounded-full border-4 border-warning" style={{ width: '300px', height: '300px' }}>
                  <img
                    src={photoProfile}
                    alt={t('expertise.founder.title')}
                    style={{
                      objectFit: 'cover',
                      objectPosition: 'center',
                      width: '100%',
                      height: '100%'
                    }}
                  />
                </div>
              </div>
            </div>
            
            <div className="md:col-span-8 p-6 md:p-8 flex flex-col justify-center">
              <h3 className="hidden md:block text-3xl font-bold text-gray-900 mb-4">
                {t('expertise.founder.greeting', 'Bonjour, je suis')} Raouf K.
              </h3>
              <h4 className="hidden md:block text-xl font-semibold text-primary mb-6">
                {t('expertise.founder.role', 'Parajuriste en droit familial')}
              </h4>
              
              <div className="prose prose-lg max-w-none text-gray-600">
                {t('expertise.founder.bio', { returnObjects: true })?.map((paragraph, index) => (
                  <p key={index} className="mb-4">{paragraph}</p>
                )) || (
                  <>
                    <p className="mb-4">
                      <span className="font-semibold">{t('expertise.founder.mission', 'Diplômé en droit français et en route vers le barreau, j\'ai fondé clicdroits.info après avoir constaté un besoin criant à l\'aide juridique : des outils accessibles pour naviguer le système judiciaire sans se ruiner.')}</span>
                    </p>
                    <p className="mb-4">
                      {t('expertise.founder.experience', 'Mon approche : transformer la complexité juridique en ressources exploitables. Outils d\'estimation de délais, de frais et de pension alimentaire, guides et frise interactive de votre procédure – des outils concrets pour vous permettre de prendre des décisions sans dépendre entièrement d\'interventions coûteuses.')}
                    </p>
                    <p className="mb-6">
                      {t('expertise.founder.creation', 'clicdroits.info vous offre des outils concrets, pas des paroles réconfortantes.')}
                    </p>
                  </>
                )}
              </div>
              
              <div className="mt-6 flex justify-center md:justify-start">
                <a 
                  href="#tarifs" 
                  className="inline-flex items-center px-6 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:scale-105"
                  onClick={() => document.getElementById('tarifs').scrollIntoView({ behavior: 'smooth' })}
                >
                  {t('expertise.cta', 'Découvrir comment je peux vous aider')}
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="bg-primary text-white rounded-xl p-8 md:p-10 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-warning/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-white/20 rounded-full blur-3xl"></div>
          
          <div className="relative z-10">
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                {t('expertise.difference', 'Ce qui fait la différence avec clicdroits.info')}
              </h3>
              <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto">
                {t('expertise.notLawyer', "Je ne suis pas avocat, mais un parajuriste spécialisé qui a une mission claire :")}{' '}
                <span className="font-bold">{t('expertise.mission', 'simplifier le droit familial et le rendre accessible à tous')}</span>.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                <h4 className="text-xl font-bold mb-3 text-warning">{t('expertise.approach', 'Approche pratique')}</h4>
                <p className="text-white/90">{t('expertise.approachDesc', 'Des informations pratiques basées sur des cas réels, pas de jargon juridique ou de théorie complexe.')}</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                <h4 className="text-xl font-bold mb-3 text-warning">{t('expertise.support', 'Support continu')}</h4>
                <p className="text-white/90">{t('expertise.supportDesc', 'Un accès permanent à votre espace membre et des mises à jour régulières des guides et outils d\'estimation.')}</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ExpertiseAffirmation;
